import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { fetchTowns, fetchVolumes, suggestKeywords } from '../utils/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function StepIndicator({ step }) {
  const labels = ['Services', 'Locations', 'Results'];
  return (
    <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem', alignItems: 'center' }}>
      {labels.map((label, i) => {
        const n = i + 1; const isDone = step > n; const isActive = step === n;
        return (
          <React.Fragment key={n}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <div style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: isDone ? '#3B6D11' : isActive ? '#185FA5' : 'var(--gray-200)', color: isDone || isActive ? '#fff' : 'var(--gray-500)' }}>
                {isDone ? '✓' : n}
              </div>
              <span style={{ fontSize: 13, color: isActive ? 'var(--gray-900)' : 'var(--gray-500)', fontWeight: isActive ? 500 : 400 }}>{label}</span>
            </div>
            {i < labels.length - 1 && <div style={{ flex: 1, height: 1, background: 'var(--gray-200)', margin: '0 12px' }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function VolumeChecker({ onBack }) {
  const [step, setStep] = useState(1);

  // Step 1 — services
  const [services, setServices] = useState(['']);
  const [generateVariations, setGenerateVariations] = useState(false);
  const [locationCode, setLocationCode] = useState(2826);
  const [generatingKws, setGeneratingKws] = useState(false);

  // Step 2 — locations
  const [locationMode, setLocationMode] = useState('postcode');
  const [postcode, setPostcode] = useState('');
  const [radius, setRadius] = useState(10);
  const [towns, setTowns] = useState([]);
  const [selectedTowns, setSelectedTowns] = useState([]);
  const [loadingTowns, setLoadingTowns] = useState(false);
  const [townError, setTownError] = useState('');
  const [newTown, setNewTown] = useState('');
  const [pasteInput, setPasteInput] = useState('');

  // Step 3 — results
  const [keywords, setKeywords] = useState([]);
  const [results, setResults] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [sortBy, setSortBy] = useState('volume');
  const [filterMin, setFilterMin] = useState(0);
  const [exporting, setExporting] = useState(false);

  const [pasteServices, setPasteServices] = useState('');
  const [showPasteServices, setShowPasteServices] = useState(false);

  const addService = () => setServices(p => [...p, '']);
  const removeService = (i) => setServices(p => p.filter((_, idx) => idx !== i));
  const updateService = (i, v) => setServices(p => p.map((s, idx) => idx === i ? v : s));

  const parsePastedServices = () => {
    const parsed = pasteServices
      .split(/[\n,;]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .filter((s, i, arr) => arr.indexOf(s) === i);
    if (!parsed.length) return;
    setServices(parsed);
    setPasteServices('');
    setShowPasteServices(false);
  };

  const handleLoadTowns = async () => {
    if (!postcode.trim()) return;
    setLoadingTowns(true);
    setTownError('');
    try {
      const res = await fetchTowns(postcode.trim(), radius);
      setTowns(res.towns || []);
      setSelectedTowns(res.towns || []);
    } catch (e) {
      setTownError('Could not fetch towns — check the postcode and try again.');
    }
    setLoadingTowns(false);
  };

  const parsePastedTowns = () => {
    const parsed = pasteInput
      .split(/[\n,;]+/)
      .map(t => t.trim())
      .filter(t => t.length > 0)
      .filter((t, i, arr) => arr.indexOf(t) === i);
    if (!parsed.length) { setTownError('No towns found — check your list and try again.'); return; }
    setTowns(parsed);
    setSelectedTowns(parsed);
    setPasteInput('');
    setTownError('');
  };

  const switchLocationMode = (newMode) => {
    setLocationMode(newMode);
    setTowns([]);
    setSelectedTowns([]);
    setTownError('');
  };

  const toggleTown = (t) => setSelectedTowns(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]);

  const addTown = () => {
    const v = newTown.trim();
    if (v && !towns.includes(v)) { setTowns(p => [...p, v]); setSelectedTowns(p => [...p, v]); setNewTown(''); }
  };

  const handleFetchVolumes = async () => {
    const cleanServices = services.filter(s => s.trim());
    if (!cleanServices.length || (locationMode !== 'none' && !selectedTowns.length)) return;

    setFetching(true);
    setFetchError('');
    setResults([]);

    try {
      let kwList = [];

      if (generateVariations) {
        setGeneratingKws(true);
        const result = await suggestKeywords(cleanServices, '');
        setGeneratingKws(false);
        Object.entries(result.suggestions).forEach(([service, kws]) => {
          kws.forEach(kw => {
            selectedTowns.forEach(town => {
              kwList.push({ keyword: `${kw} ${town.toLowerCase()}`, service, location: town, baseKw: kw });
            });
          });
        });
      } else if (locationMode === 'none') {
        cleanServices.forEach(service => {
          kwList.push({ keyword: service.trim().toLowerCase(), service: service.trim(), location: '', baseKw: service.trim() });
        });
      } else {
        cleanServices.forEach(service => {
          selectedTowns.forEach(town => {
            kwList.push({ keyword: `${service.trim().toLowerCase()} ${town.toLowerCase()}`, service: service.trim(), location: town, baseKw: service.trim() });
          });
        });
      }

      setKeywords(kwList);

      const kwStrings = kwList.map(k => k.keyword);
      const volumeData = await fetchVolumes(kwStrings, locationCode);

      const mapped = kwList.map(item => {
        const normKw = item.keyword.toLowerCase().trim().replace(/\s+/g, ' ');
        const volInfo = volumeData.volumes?.[normKw] || volumeData.volumes?.[item.keyword] || {};
        return {
          keyword: item.keyword,
          service: item.service,
          location: item.location,
          volume: volInfo.searchVolume || 0,
        };
      });

      setResults(mapped);
      setStep(3);
    } catch (e) {
      setFetchError('Failed to fetch volumes — please try again.');
      console.error(e);
    }
    setFetching(false);
  };

  const sorted = [...results]
    .filter(r => r.volume >= filterMin)
    .sort((a, b) => {
      if (sortBy === 'volume') return b.volume - a.volume;
      if (sortBy === 'keyword') return a.keyword.localeCompare(b.keyword);
      if (sortBy === 'location') return a.location.localeCompare(b.location);
      return 0;
    });

  const exportExcel = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/export/volume-checker`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keywords: sorted,
          reportDate: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
          services: [...new Set(sorted.map(r => r.service))],
        })
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `IMSR-search-volumes-${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    }
    setExporting(false);
  };

  const totalWithVolume = results.filter(r => r.volume > 0).length;
  const avgVolume = results.length ? Math.round(results.reduce((a, r) => a + r.volume, 0) / results.length) : 0;
  const topVolume = results.length ? Math.max(...results.map(r => r.volume)) : 0;

  // ── Step 1 ──────────────────────────────────────────────────
  if (step === 1) return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
        <button className="btn btn-sm" onClick={onBack}>← Tools</button>
        <div>
          <div className="page-title">Search Volume Checker</div>
          <div className="page-sub">Check monthly search volumes for service + location keyword combinations</div>
        </div>
      </div>
      <StepIndicator step={1} />

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title">Services</div>
        <div className="card-sub">Enter each service you want to check. These will be combined with locations to build your keyword list.</div>
        {services.map((s, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <input type="text" value={s} placeholder="e.g. carpet cleaning" onChange={e => updateService(i, e.target.value)} style={{ flex: 1 }} />
            {services.length > 1 && <button className="btn btn-sm" onClick={() => removeService(i)} style={{ color: 'var(--gray-400)', borderColor: 'transparent' }}>✕</button>}
          </div>
        ))}
        <button className="btn btn-sm" onClick={addService} style={{ marginTop: 4 }}>+ Add one</button>
        <button className="btn btn-sm" onClick={() => setShowPasteServices(p => !p)} style={{ marginTop: 4, marginLeft: 8 }}>
          {showPasteServices ? 'Cancel' : '+ Paste multiple'}
        </button>
        {showPasteServices && (
          <div style={{ marginTop: 12, padding: '12px', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)', border: '1px solid var(--gray-200)' }}>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 8 }}>Paste a list of services — one per line, or separated by commas</div>
            <textarea
              rows={5}
              placeholder="Carpet cleaning&#10;Upholstery cleaning&#10;Oven cleaning&#10;End of tenancy cleaning"
              value={pasteServices}
              onChange={e => setPasteServices(e.target.value)}
              style={{ marginBottom: 8, fontFamily: 'inherit', fontSize: 13 }}
              autoFocus
            />
            <button className="btn btn-sm btn-primary" onClick={parsePastedServices} disabled={!pasteServices.trim()}>
              Add services
            </button>
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title">Keyword variations</div>
        <div className="card-sub">Choose whether to check just the service name or generate AI variations.</div>
        <div style={{ display: 'flex', gap: 12 }}>
          {[
            { val: false, label: 'Service name only', desc: 'e.g. "carpet cleaning bromley"' },
            { val: true, label: 'Generate AI variations', desc: 'e.g. "carpet cleaner bromley", "carpet cleaning services bromley"' },
          ].map(opt => (
            <label key={String(opt.val)} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', flex: 1, padding: '10px 12px', borderRadius: 'var(--radius-md)', border: `1px solid ${generateVariations === opt.val ? 'var(--blue-border)' : 'var(--gray-200)'}`, background: generateVariations === opt.val ? 'var(--blue-light)' : 'var(--gray-50)' }}>
              <input type="radio" checked={generateVariations === opt.val} onChange={() => setGenerateVariations(opt.val)} style={{ marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{opt.label}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{opt.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card-title">Country</div>
        <select value={locationCode} onChange={e => setLocationCode(parseInt(e.target.value))} style={{ maxWidth: 260 }}>
          <option value={2826}>United Kingdom</option>
          <option value={2840}>United States</option>
          <option value={2036}>Australia</option>
          <option value={2124}>Canada</option>
          <option value={2372}>Ireland</option>
        </select>
      </div>

      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn btn-primary" onClick={() => setStep(2)} disabled={!services.filter(s => s.trim()).length}>
          Next: Locations →
        </button>
      </div>
    </div>
  );

  // ── Step 2 ──────────────────────────────────────────────────
  if (step === 2) return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
        <button className="btn btn-sm" onClick={onBack}>← Tools</button>
        <div>
          <div className="page-title">Search Volume Checker</div>
          <div className="page-sub">Check monthly search volumes for service + location keyword combinations</div>
        </div>
      </div>
      <StepIndicator step={2} />

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title">Service area</div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          {[
            { val: 'postcode', label: 'Postcode radius', desc: 'Auto-fetch towns from a postcode' },
            { val: 'manual', label: 'Paste town list', desc: 'Paste or type towns directly' },
            { val: 'none', label: 'No location', desc: 'Check keywords without locations' },
          ].map(opt => (
            <label key={opt.val} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', flex: 1, padding: '10px 12px', borderRadius: 'var(--radius-md)', border: `1px solid ${locationMode === opt.val ? 'var(--blue-border)' : 'var(--gray-200)'}`, background: locationMode === opt.val ? 'var(--blue-light)' : 'var(--gray-50)' }}>
              <input type="radio" name="volMode" value={opt.val} checked={locationMode === opt.val} onChange={() => switchLocationMode(opt.val)} style={{ marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{opt.label}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{opt.desc}</div>
              </div>
            </label>
          ))}
        </div>

        {locationMode === 'postcode' && (
          <>
            <div className="form-row form-row-2 form-group">
              <div>
                <label>Postcode</label>
                <input type="text" placeholder="e.g. BR3 1PP" value={postcode} onChange={e => setPostcode(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLoadTowns()} />
              </div>
              <div>
                <label>Radius</label>
                <select value={radius} onChange={e => setRadius(parseInt(e.target.value))}>
                  <option value={1}>1 mile</option>
                  <option value={5}>5 miles</option>
                  <option value={10}>10 miles</option>
                  <option value={15}>15 miles</option>
                  <option value={20}>20 miles</option>
                  <option value={30}>30 miles</option>
                  <option value={50}>50 miles</option>
                </select>
              </div>
            </div>
            <button className="btn" onClick={handleLoadTowns} disabled={loadingTowns || !postcode.trim()}>
              {loadingTowns ? <><span className="spinner" /> Fetching...</> : 'Fetch towns within radius'}
            </button>
          </>
        )}

        {locationMode === 'none' && (
          <div className="alert alert-info">
            Keywords will be checked exactly as entered — no location will be appended.
          </div>
        )}

        {locationMode === 'manual' && (
          <>
            <div className="card-sub" style={{ marginBottom: 10 }}>Paste towns separated by new lines, commas or semicolons.</div>
            <textarea
              rows={6}
              placeholder="Bromley, Beckenham, Orpington, Chislehurst, Petts Wood"
              value={pasteInput}
              onChange={e => setPasteInput(e.target.value)}
              style={{ marginBottom: 10, fontFamily: 'inherit' }}
            />
            <button className="btn" onClick={parsePastedTowns} disabled={!pasteInput.trim()}>
              Add towns
            </button>
          </>
        )}

        {townError && <div className="alert alert-warning" style={{ marginTop: 10 }}>{townError}</div>}

        {towns.length > 0 && locationMode !== 'none' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, marginBottom: 8 }}>
              <div className="section-label" style={{ margin: 0 }}>{selectedTowns.length} of {towns.length} towns selected</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-sm" onClick={() => setSelectedTowns([...towns])}>All</button>
                <button className="btn btn-sm" onClick={() => setSelectedTowns([])}>None</button>
              </div>
            </div>
            <div className="chip-grid">
              {towns.map((t, i) => (
                <span key={i} className={`chip ${selectedTowns.includes(t) ? 'selected' : ''}`} style={{ paddingRight: 6 }}>
                  <span onClick={() => toggleTown(t)}>{t}</span>
                  <span onClick={() => { setTowns(p => p.filter(x => x !== t)); setSelectedTowns(p => p.filter(x => x !== t)); }} style={{ marginLeft: 4, opacity: 0.4, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>x</span>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input type="text" placeholder="Add a town manually..." value={newTown} onChange={e => setNewTown(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTown()} style={{ maxWidth: 220 }} />
              <button className="btn btn-sm" onClick={addTown} disabled={!newTown.trim()}>Add</button>
            </div>
          </>
        )}
      </div>

      {fetchError && <div className="alert alert-error">{fetchError}</div>}

      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn" onClick={() => setStep(1)}>Back</button>
        <button className="btn btn-primary" onClick={handleFetchVolumes} disabled={fetching || (locationMode !== 'none' && !selectedTowns.length)}>
          {fetching
            ? <><span className="spinner" /> {generatingKws ? 'Generating keywords...' : 'Fetching volumes...'}</>
            : `Fetch volumes for ${services.filter(s => s.trim()).length} service${services.filter(s => s.trim()).length !== 1 ? 's' : ''} x ${selectedTowns.length} location${selectedTowns.length !== 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );

  // ── Step 3 ──────────────────────────────────────────────────
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
        <button className="btn btn-sm" onClick={onBack}>← Tools</button>
        <div>
          <div className="page-title">Search Volume Checker</div>
          <div className="page-sub">Check monthly search volumes for service + location keyword combinations</div>
        </div>
      </div>
      <StepIndicator step={3} />

      <div className="metric-grid" style={{ marginBottom: '1rem' }}>
        <div className="metric-card"><div className="metric-label">Total keywords</div><div className="metric-value">{results.length}</div></div>
        <div className="metric-card"><div className="metric-label">With search volume</div><div className="metric-value" style={{ color: 'var(--green)' }}>{totalWithVolume}</div></div>
        <div className="metric-card"><div className="metric-label">Avg. monthly searches</div><div className="metric-value">{avgVolume.toLocaleString()}</div></div>
        <div className="metric-card"><div className="metric-label">Highest volume</div><div className="metric-value">{topVolume.toLocaleString()}</div></div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ margin: 0, whiteSpace: 'nowrap' }}>Sort by:</label>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ width: 'auto' }}>
                <option value="volume">Search volume</option>
                <option value="keyword">Keyword A-Z</option>
                <option value="location">Location A-Z</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ margin: 0, whiteSpace: 'nowrap' }}>Min. volume:</label>
              <select value={filterMin} onChange={e => setFilterMin(parseInt(e.target.value))} style={{ width: 'auto' }}>
                <option value={0}>Show all</option>
                <option value={10}>10+</option>
                <option value={50}>50+</option>
                <option value={100}>100+</option>
                <option value={500}>500+</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm" onClick={() => { setStep(1); setResults([]); }}>Start over</button>
            <button className="btn btn-primary" onClick={exportExcel} disabled={exporting}>
              {exporting ? <><span className="spinner" /> Generating...</> : 'Download Excel'}
            </button>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="kw-table">
          <thead>
            <tr>
              <th>Keyword</th>
              <th>Service</th>
              <th>Location</th>
              <th className="num">Monthly searches</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr key={i}>
                <td>{r.keyword}</td>
                <td style={{ color: 'var(--gray-500)' }}>{r.service}</td>
                <td style={{ color: 'var(--gray-500)' }}>{r.location}</td>
                <td className="num">
                  <span style={{ fontWeight: 600, color: r.volume >= 500 ? 'var(--green)' : r.volume >= 100 ? 'var(--blue-mid)' : r.volume > 0 ? 'var(--gray-700)' : 'var(--gray-400)' }}>
                    {r.volume > 0 ? r.volume.toLocaleString() : '—'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sorted.length === 0 && (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-400)', fontSize: 13 }}>
            No keywords match the current filter.
          </div>
        )}
      </div>
    </div>
  );
}
